<template>
  <div class="d-flex justify-center">
    <AppMenuAsPopover offset-x
                      top
                      :card-min-width="330"
                      :card-max-width="330"
                      @open="onMenuOpen"
    >
      <template #activator="{ props }">
        <app-button
          type="ghost"
          variant="neutral"
          size="small"
          full-width
          v-bind="props"
        >
          <template #left-icon>
            <app-material-icon name="add" />
          </template>
          {{ $t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.createStatus') }}
        </app-button>
      </template>
      <template #default="{closeMenu}">
        <v-card-title class="newStatus__card__title text-text-primary heading-sm-bold d-flex align-center justify-space-between">
          {{ $t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.createStatus') }}
          <app-button
            type="icon"
            @click="closeMenu"
          >
            <app-material-icon name="close" />
          </app-button>
        </v-card-title>
        <v-card-text class="text--primary pt-4">
          <v-row>
            <v-col>
              <div class="mb-4 heading-xs-bold text-text-secondary">{{$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusNameLabel')}}</div>
              <AppTextField v-model="v$.newStatus.label.$model"
                            class="required"
                            :error-messages="labelErrors"
                            :placeholder="$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusNamePlaceholder')"
                            :counter="45"
                            :maxlength="45"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="mb-4 heading-xs-bold text-text-secondary">{{$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusCategoryLabel')}}</div>
              <AppSelect v-model="v$.newStatus.category.$model"
                         :items="taskStatusCategoriesList"
                         item-value="name"
                         :error-messages="categoryErrors"
                         class="required"
              >
                <template #item="{item, props}">
                  <v-list-item v-bind="props">
                    <template #title>
                      {{ $t(`common.statusCategoryNames.${item.raw.name}`) }}
                    </template>
                  </v-list-item>
                </template>
                <template #selection="{item}">
                  {{ $t(`common.statusCategoryNames.${item.raw.name}`) }}
                </template>
              </AppSelect>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="mb-4 heading-xs-bold text-text-secondary">{{$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusColorLabel')}}</div>
              <ChecklistStatusDialogColorPicker v-model="v$.newStatus.color.$model"/>
              <span v-if="colorErrors.length"
                    class="detail-md-regular text-error">
              {{ colorErrors[0] }}
            </span>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="text-right">
              <div class="d-flex justify-end ga-4">
                <app-button
                  type="outlined"
                  variant="neutral"
                  :disabled="postStatusPending"
                  @click="closeMenu"
                >
                  {{ $t('common.cancel') }}
                </app-button>
                <app-button
                  :loading="postStatusPending"
                  @click="preparePostStatus(closeMenu)"
                >
                  {{ $t('common.register') }}
                </app-button>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </template>
    </AppMenuAsPopover>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppMenuAsPopover from '@/common/AppMenuAsPopover.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import ChecklistStatusDialogColorPicker from '@/project/checklist/dialogs/ChecklistStatusDialogColorPicker.vue'
import { POST_STATUS } from '@/store/modules/checklist/action_types'


export default {
  name: 'ChecklistStatusDialogNewStatus',
  components: { ChecklistStatusDialogColorPicker, AppSelect, AppTextField, AppMenuAsPopover },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  validations: {
    newStatus: {
      label: { required },
      category: { required },
      color: { required },
    },
  },
  data () {
    return {
      newStatus: {
        label: '',
        category: this.category,
        color: '',
      },
    }
  },
  computed: {
    ...mapState('checklist', ['postStatusPending', 'taskStatusCategories']),
    ...mapGetters('checklist', ['taskStatusCategoriesList']),
    ...mapGetters('room', ['roomMnemo']),
    labelErrors () {
      return formFieldErrors(
        this.v$.newStatus.label,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusNameLabel') })],
        ],
      )
    },
    categoryErrors () {
      return formFieldErrors(
        this.v$.newStatus.category,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusCategoryLabel') })],
        ],
      )
    },
    colorErrors () {
      return formFieldErrors(
        this.v$.newStatus.color,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusColorLabel') })],
        ],
      )
    },
  },
  methods: {
    ...mapActions('checklist', [POST_STATUS]),
    onMenuOpen () {
      this.v$.$reset()
      this.newStatus = {
        label: '',
        category: this.category,
        color: '',
      }
    },
    async preparePostStatus (closeMenuFunction) {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        const statusCategoriesToPost = cloneDeep(this.taskStatusCategories)
        statusCategoriesToPost[this.newStatus.category].unshift({
          label: this.newStatus.label,
          color: this.newStatus.color,
        })

        try {
          await this.POST_STATUS({
            mnemo: this.roomMnemo,
            data: { statuses: statusCategoriesToPost },
          })
        } catch (error) {
          console.error(error)
          toast.error(this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.postStatusError'))
        } finally {
          closeMenuFunction()
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.newStatus {
  &__card {
    &__title {
      border-bottom: get-border();
    }
  }
}
:deep(label.v-label.v-field-label){
  display: none !important
}
</style>
